/**
 * Copyright Mántum All Rights Reserved.
 */
import Input from '../../form/input';
import useOutside from '../../../hooks/useOutside';
import { color as generate } from '@mantum/kanela';

import {
    Suspense,
    forwardRef,
    lazy,
    useEffect,
    useImperativeHandle,
    useRef,
    useState
} from 'react';

import type { ColorResult } from 'react-color';
import type { Detail, Validation } from '@mantum/kanela';

const Picker = lazy(() => import('./library'));

export type PropTypes = {
    advanced?: {
        allowInput?: boolean;
        mode?: 'rgb' | 'hex';
    };
    disabled?: boolean;
    error?: boolean;
    message?: string;
    name?: string;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onChange?: (name?: string, value?: unknown) => void;
    optional?: boolean;
    placeholder?: string;
    testid?: string;
    validation?: Validation;
    value?: string | 'random';
};

export default forwardRef<Detail<unknown>, PropTypes>(
    function ColorPicker(props, ref) {
        // prettier-ignore
        const {
            advanced = {},
            disabled = false,
            name,
            optional = true
        } = props;

        // prettier-ignore
        const { 
            allowInput = true,
            mode = 'hex'
        } = advanced;

        const changeRef = useRef(props.onChange);
        const containerRef = useRef<HTMLDivElement>(null);
        const validationRef = useRef<Detail<string>>(null);

        const [showPicker, setShowPicker] = useState(false);

        const [color, setColor] = useState<string | undefined>();

        const getColor = (value?: string) =>
            value === 'random' ? generate() : value;

        useEffect(() => {
            setColor(getColor(props.value));
        }, [props.value]);

        useEffect(() => {
            if (changeRef.current) {
                changeRef.current(name, getColor(props.value));
            }
        }, [name, props.value]);

        useEffect(() => {
            changeRef.current = props.onChange;
        }, [props.onChange]);

        const handleClick = () => {
            if (!disabled) {
                setShowPicker(true);
            }
        };

        const handleSelectColor = (color: ColorResult): void => {
            const value =
                mode === 'hex'
                    ? color.hex
                    : `rgba(${Object.entries(color.rgb)
                          .map((it) => it[1])
                          .join(', ')})`;

            setColor(value);
            if (changeRef.current) {
                changeRef.current(props.name, value);
            }
        };

        const handleChangeColor = (name?: string, value?: unknown): void => {
            setColor(value as string);
            if (changeRef.current) {
                changeRef.current(name, value);
            }
        };

        const handleOnBlur = (e: React.FocusEvent<HTMLInputElement>): void => {
            setColor(e.target.value);
            if (props.onBlur) {
                props.onBlur(e);
            }
        };

        useOutside(containerRef, () => {
            setShowPicker(false);
        });

        useImperativeHandle(
            ref,
            () => {
                return {
                    name: validationRef.current?.name,
                    validable: validationRef.current?.validable ?? true,
                    clear() {
                        setColor('');
                    },
                    validate() {
                        const result = validationRef.current?.validate();
                        if (result) {
                            return true;
                        }
                        return false;
                    },
                    value() {
                        return validationRef.current?.value();
                    }
                };
            },
            []
        );

        const cursor = !disabled ? 'cursor-pointer' : 'cursor-default';

        return (
            <div className='flex gap-2'>
                <button
                    type='button'
                    className={`h-10 w-10 rounded bg-gray-200 ${cursor}`}
                    onClick={handleClick}
                    style={{ backgroundColor: color }}
                />
                {allowInput && (
                    <div className='flex-grow'>
                        <Input
                            disabled={disabled}
                            message={props.validation?.message}
                            name={props.name}
                            onBlur={handleOnBlur}
                            onChange={handleChangeColor}
                            optional={optional}
                            placeholder={props.placeholder}
                            ref={validationRef}
                            testid={props.testid}
                            type='text'
                            value={color}
                        />
                    </div>
                )}
                {!disabled && showPicker && (
                    <div
                        className='absolute z-50 text-center'
                        ref={containerRef}
                    >
                        <Suspense>
                            <Picker
                                color={color}
                                onChange={handleSelectColor}
                                width='240px'
                                disableAlpha={mode === 'hex'}
                            />
                        </Suspense>
                    </div>
                )}
            </div>
        );
    }
);
